import { AxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import {
  AlertTicketPayload,
  archiveAlert,
  createAlert,
  createRuleBoard,
  deleteAlert,
  deleteAlertByCustomViewId,
  deleteMultipleAlert,
  deleteRuleBoard,
  deleteTeamsWebHook,
  getAlertByID,
  getAlertByTargetID,
  getAlertConfigs,
  getAlertSearchSuggestion,
  getAlertSummary,
  getAlerts,
  getRegisteredChannels,
  getRuleBoardById,
  getRuleBoardByIdV2,
  getRuleBoardList,
  muteRuleBoard,
  patchAlert,
  registerTeamsWebHook,
  snoozeAlert,
  testTeamsWebHook,
  updateAlert,
  updateRuleBoard,
  updateTeamsWebHook,
} from '../services/alerts';

import { MuteData, RuleBoard, RuleBoardData } from '@controlrooms/models';

export const useTeamsWebhookChannels = () => {
  return useQuery('registeredTeamsChannels', () => getRegisteredChannels());
};

export const useGetAlertSummary = (target_type: string, target_id: string | undefined) => {
  return useQuery(
    ['ALERT_SUMMARY', target_id],
    () => getAlertSummary({ targetType: target_type, targetId: target_id || '' }),
    {
      cacheTime: 10,
      enabled: !!target_id,
    },
  );
};

export const useAlertsAPI = () => {
  const queryClient = useQueryClient();

  const createAlertMutation = useMutation(createAlert, {
    onSuccess: () => queryClient.invalidateQueries('alerts'),
  });

  const getAlertsMutation = useMutation(getAlerts, {
    onSuccess: () => queryClient.invalidateQueries('alerts'),
  });

  const getAlertByIDMutation = useMutation(getAlertByID, {
    onSuccess: () => queryClient.invalidateQueries('alerts'),
  });

  const getAlertByTargetIDMutation = useMutation(getAlertByTargetID, {
    onSuccess: () => queryClient.invalidateQueries('alerts'),
  });

  const getAlertSummaryMutation = useMutation(getAlertSummary, {
    onSuccess: () => queryClient.invalidateQueries('alerts'),
  });

  const getAlertSearchSuggestionMutation = useMutation(getAlertSearchSuggestion, {
    onSuccess: () => queryClient.invalidateQueries('alerts'),
  });

  const updateAlertMutation = useMutation(updateAlert, {
    onSuccess: () => queryClient.invalidateQueries('alerts'),
  });

  const patchAlertMutation = useMutation(patchAlert, {
    onSuccess: () => queryClient.invalidateQueries('alerts'),
  });

  const deleteAlertMutation = useMutation(deleteAlert, {
    onSuccess: () => queryClient.invalidateQueries('alerts'),
  });

  const deleteMultipleAlertMutation = useMutation(deleteMultipleAlert, {
    onSuccess: () => queryClient.invalidateQueries('alerts'),
  });

  const getRegisteredChannelsMutation = useMutation(getRegisteredChannels, {
    onSuccess: () => queryClient.invalidateQueries('alerts'),
  });
  // const useAlertSearchSuggestion = (params: any) =>
  //   useQuery(['searchSuggestion', params], () => getAlertSearchSuggestion(params));

  const deleteAlertByCustomViewMutation = useMutation(deleteAlertByCustomViewId, {
    onSuccess: () => queryClient.invalidateQueries('alerts'),
  });

  const registerTeamsWebHookMutation = useMutation(registerTeamsWebHook, {
    onSuccess: () => queryClient.invalidateQueries('registeredTeamsChannels'),
  });

  const updateTeamsWebHookMutation = useMutation(updateTeamsWebHook, {
    onSuccess: () => queryClient.invalidateQueries('registeredTeamsChannels'),
  });

  const deleteTeamsWebHookMutation = useMutation(deleteTeamsWebHook, {
    onSuccess: () => queryClient.invalidateQueries('registeredTeamsChannels'),
  });

  const testTeamsWebHookMutation = useMutation(testTeamsWebHook);

  return {
    createAlertMutation,
    getAlertsMutation,
    getAlertByIDMutation,
    getAlertByTargetIDMutation,
    getAlertSummaryMutation,
    getAlertSearchSuggestionMutation,
    updateAlertMutation,
    patchAlertMutation,
    deleteAlertMutation,
    deleteMultipleAlertMutation,
    getRegisteredChannelsMutation,
    deleteAlertByCustomViewMutation,
    registerTeamsWebHookMutation,
    deleteTeamsWebHookMutation,
    updateTeamsWebHookMutation,
    testTeamsWebHookMutation,
    // useAlertSearchSuggestion,
  };
};

export const useGetAlertByID = (ruleId: string) => {
  return useQuery(['ALERT_BY_ID', ruleId], () => getAlertByID(Number(ruleId)), {
    enabled: !!ruleId,
    cacheTime: 2,
  });
};

export const useFetchAlertConfigs = (target_type: string, target_id: string | undefined) => {
  return useQuery(
    ['ALERT_CONFIGS', target_id],
    () => getAlertConfigs({ targetType: target_type.toLowerCase(), targetId: target_id || '' }),
    {
      cacheTime: 2,
      enabled: !!target_id,
    },
  );
};

export const useGetRuleBoard = () => {
  return useQuery(['ALERT_BOARDS'], getRuleBoardList, {
    retry: 0,
    select: (ruleBoards: RuleBoard[]) => ruleBoards,
  });
};
export const useGetAlertsForBoard = (boardId: string) => {
  return useQuery(['ALERTS_BY_BOARD_ID', boardId], () => getRuleBoardById(boardId));
};

export const useGetAlertsForBoardV2 = (
  boardId: string,
  timeSelection?: { fromTime?: string; toTime?: string; duration?: number },
  options?: { enabled?: boolean; isLiveMode?: boolean },
) => {
  return useQuery(
    [
      'ALERTS_BY_BOARD_ID',
      boardId,
      timeSelection?.fromTime,
      timeSelection?.toTime,
      timeSelection?.duration,
    ],
    () =>
      getRuleBoardByIdV2(
        boardId,
        timeSelection?.fromTime,
        timeSelection?.toTime,
        timeSelection?.duration,
      ),
    {
      enabled: options?.enabled !== false,
      refetchInterval: options?.isLiveMode ? 30000 : false, // Refetch every 30 seconds only in live mode
      keepPreviousData: true, // Keep previous data while fetching new data
    },
  );
};

export const useCreateRuleBoard = (
  onSuccess?: (data: string) => Promise<unknown> | void,
  onError?: (error: AxiosError) => Promise<unknown> | void,
) => {
  const queryClient = useQueryClient();
  return useMutation((data: RuleBoardData) => createRuleBoard(data), {
    onSuccess() {
      queryClient.invalidateQueries(['ALERT_BOARDS']);
    },
    onError,
  });
};

export const useUpdateRuleBoard = () => {
  const queryClient = useQueryClient();
  return useMutation((data: RuleBoardData) => updateRuleBoard(data), {
    onSuccess() {
      queryClient.invalidateQueries(['ALERT_BOARDS']);
    },
  });
};

export const useMuteRuleBoard = () => {
  const queryClient = useQueryClient();
  return useMutation((data: MuteData) => muteRuleBoard(data.rule_id, data), {
    onSuccess() {
      queryClient.invalidateQueries(['ALERTS_BY_BOARD_ID']);
    },
  });
};

export const useArchiveAlert = () => {
  const queryClient = useQueryClient();
  return useMutation((data: AlertTicketPayload) => archiveAlert(data), {
    onSuccess() {
      queryClient.invalidateQueries(['ALERTS_BY_BOARD_ID']);
    },
  });
};

export const useSnoozeAlert = () => {
  const queryClient = useQueryClient();
  return useMutation((data: AlertTicketPayload) => snoozeAlert(data), {
    onSuccess() {
      queryClient.invalidateQueries(['ALERTS_BY_BOARD_ID']);
    },
  });
};

export const useDeleteRuleBoard = () => {
  const queryClient = useQueryClient();
  return useMutation((id: string) => deleteRuleBoard(id), {
    onSuccess() {
      queryClient.invalidateQueries(['ALERT_BOARDS']);
    },
  });
};

export const useGetAlertConfigs = ({
  alertType,
  targetId,
}: {
  alertType: string;
  targetId: string;
}) => {
  const queryString = `filter_by=${alertType.toLowerCase()}&filter_text=${targetId}`;
  return useQuery({
    queryKey: ['ALERT_CONFIGS', targetId],
    queryFn: () => getAlerts(queryString),
    staleTime: 60 * 1000,
  });
};
